import React, { useEffect, useRef, useState } from 'react';
import WaveSurfer from 'wavesurfer.js';

const BTN_SIZE = 22; // px — play button square
const BTN_GAP  = 5;  // px — gap between button and waveform

/**
 * AudioWaveformCell — renders a real WaveSurfer waveform with a play/stop
 * button, for use inside an SVG <foreignObject>. Matches the AudioField config.
 *
 * Props:
 *   srcUrl  – audio file URL (null → dashed placeholder, no button)
 *   height  – total pixel height (default 26)
 */
export default function AudioWaveformCell({ srcUrl, height = 26, onSelect }) {
  const containerRef   = useRef(null);
  const wsRef          = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [ready, setReady]         = useState(false);

  useEffect(() => {
    if (!containerRef.current || !srcUrl) return;

    const ws = WaveSurfer.create({
      container: containerRef.current,
      waveColor: '#BCA4FF',
      progressColor: '#9146FF',
      url: srcUrl,
      height,
      normalize: true,
      hideScrollbar: true,
      interact: false,
      barWidth: 2,
      barGap: 1,
      barRadius: 2,
    });

    ws.on('ready',  () => setReady(true));
    ws.on('play',   () => setIsPlaying(true));
    ws.on('pause',  () => setIsPlaying(false));
    ws.on('finish', () => { setIsPlaying(false); ws.seekTo(0); });

    wsRef.current = ws;

    return () => {
      ws.destroy();
      wsRef.current = null;
      setReady(false);
      setIsPlaying(false);
    };
  }, [srcUrl, height]);

  const handlePlay = (e) => {
    e.stopPropagation(); // don't bubble to the SVG node click (open right panel)
    const ws = wsRef.current;
    if (!ws || !ready) return;
    ws.isPlaying() ? ws.pause() : ws.play();
  };

  const handleSelect = (e) => {
    if (!onSelect) return;
    e.stopPropagation();
    onSelect();
  };

  if (!srcUrl) {
    return (
      <div
        onClick={handleSelect}
        style={{ width: '100%', height: `${height}px`, display: 'flex', alignItems: 'center', boxSizing: 'border-box', cursor: onSelect ? 'pointer' : 'default' }}
      >
        <div style={{
          width: '100%', height: '2px',
          backgroundImage: 'repeating-linear-gradient(to right, rgba(145,70,255,0.4) 0, rgba(145,70,255,0.4) 6px, transparent 6px, transparent 10px)',
          borderRadius: '1px',
        }} />
      </div>
    );
  }

  // Play icon — inline SVG triangle / stop square
  const iconSize = 8;
  const icon = isPlaying
    ? <rect x={0} y={0} width={iconSize} height={iconSize} rx={1} fill="#9146FF" />
    : <polygon points={`0,0 ${iconSize},${iconSize / 2} 0,${iconSize}`} fill="#9146FF" />;

  return (
    <div
      onClick={handleSelect}
      style={{ width: '100%', height: `${height}px`, display: 'flex', alignItems: 'center', gap: `${BTN_GAP}px`, boxSizing: 'border-box', cursor: onSelect ? 'pointer' : 'default' }}
    >
      {/* Play/stop button */}
      <button
        onClick={handlePlay}
        title={isPlaying ? 'Stop' : 'Play'}
        style={{
          flexShrink: 0,
          width:  `${BTN_SIZE}px`,
          height: `${BTN_SIZE}px`,
          minWidth: `${BTN_SIZE}px`,
          border: '1.5px solid rgba(145,70,255,0.7)',
          borderRadius: '4px',
          background: isPlaying ? 'rgba(145,70,255,0.18)' : 'rgba(145,70,255,0.08)',
          cursor: ready ? 'pointer' : 'default',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 0,
          opacity: ready ? 1 : 0.4,
          transition: 'background 0.15s',
          boxSizing: 'border-box',
        }}
      >
        <svg width={iconSize} height={iconSize} viewBox={`0 0 ${iconSize} ${iconSize}`} style={{ display: 'block' }}>
          {icon}
        </svg>
      </button>

      {/* WaveSurfer container — fills remaining width */}
      <div
        ref={containerRef}
        style={{
          flex: 1,
          height: `${height}px`,
          overflow: 'hidden',
          borderRadius: '3px',
          minWidth: 0,
          boxSizing: 'border-box',
        }}
      />
    </div>
  );
}

